import { AgentReducedRouter } from '@agent/agent-reduced/agent-reduced-router';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { BehaviorSubject, distinctUntilChanged, filter, forkJoin, map, merge, of, take, withLatestFrom } from 'rxjs';

import {
  AgentConfigFacade,
  AgentStatus,
  AgentUserFacade,
  BookingFacade,
  fTruthy,
  isNotNullOrUndefined,
  MetricService,
  Position,
  PositionCategory,
  Profile,
  ProfileFacade,
  ProfileService,
  SessionFacade,
  UntilDestroy,
  untilDestroyed,
  UserStmService,
} from '@contact/upgrade';

import { NG1_METRIC } from '@commons/core/core.provider.ajs';
import { QueueChannel } from '@contact/common-util-types';
import { AgentDesktopRouter } from './agent-desktop-router';
import { AgentDesktopState } from './agent-desktop-routing.module';
import { AgentTitleService } from './agent-title.service';

@UntilDestroy()
@Component({
  selector: 'cng-agent-desktop',
  templateUrl: './agent-desktop.component.html',
  styleUrls: ['./agent-desktop.component.scss'],
})
export class AgentDesktopComponent implements OnInit, OnDestroy {
  readonly profile$ = this.profileFacade.profile$;
  readonly position$ = this.agentUserFacade.position$;

  private readonly _restoreUrl$ = new BehaviorSubject<string | undefined>(undefined);
  readonly restoreUrl$ = this._restoreUrl$.pipe(distinctUntilChanged());

  showHelpzone = false;

  constructor(
    protected readonly actions$: Actions,
    protected readonly sessionFacade: SessionFacade,
    protected readonly profileFacade: ProfileFacade,
    protected readonly agentUserFacade: AgentUserFacade,
    protected readonly agentStatusService: UserStmService,
    protected readonly bookingFacade: BookingFacade,
    protected readonly profileService: ProfileService,
    protected readonly routerDesktop: AgentDesktopRouter,
    protected readonly routerReduced: AgentReducedRouter,
    protected readonly agentConfigFacade: AgentConfigFacade,
    protected readonly agentTitleService: AgentTitleService,
    @Inject(NG1_METRIC) private metricService: MetricService
  ) {}

  ngOnInit() {
    this.agentTitleService.init();

    // auto redirect on agent status changed
    merge(of({}), this.agentUserFacade.status$.pipe(filter(current => current && current !== AgentStatus.WORKING)))
      .pipe(
        withLatestFrom(this.restoreUrl$, this.agentConfigFacade.config$, (_, url, { initialView, channelQueues }) => ({
          url,
          defaultState: this.toDefaultPath(initialView, channelQueues),
        })),
        untilDestroyed(this)
      )
      .subscribe(({ url, defaultState }) => {
        if (url) {
          this.routerDesktop.navigateByUrl(url);
        } else {
          this.routerDesktop.navigateToTile([defaultState]);
        }
      });

    // listen for selected position action or existing position state
    const positionSelected$ = this.agentUserFacade.position$.pipe(isNotNullOrUndefined(), take(1));
    const reducedPermission$ = this.profileService.hasPermission$('CSR::self:reduced').pipe(take(1), fTruthy());
    const fromReducedRoute$ = this.routerDesktop.previousUrl$.pipe(
      take(1),
      map(url => url?.indexOf('reduced') === -1)
    );

    // redirect to reduced banner if has permission and position but not coming from reduced route (allow toggle)
    forkJoin({ positionSelected$, reducedPermission$ })
      .pipe(
        withLatestFrom(fromReducedRoute$),
        filter(([_, fromReducedRoute]) => fromReducedRoute),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.metricService.logout();
        this.routerReduced.navigateToTile();
      });

    // save the navigation state to be able restore after booking treatment
    this.routerDesktop.currentUrl$
      .pipe(
        filter(url => url?.indexOf(AgentDesktopState.BOOKING) === -1),
        untilDestroyed(this)
      )
      .subscribe(this._restoreUrl$);

    // auto redirect on the booking selection changed
    this.bookingFacade.selectedId$
      .pipe(isNotNullOrUndefined(), untilDestroyed(this))
      .subscribe(id => this.routerDesktop.navigateToTile([AgentDesktopState.BOOKING, id]));
  }

  ngOnDestroy(): void {
    this.agentTitleService.destroy();
    this._restoreUrl$.complete();
  }

  getLogin(profile: Profile): string {
    return profile.login;
  }

  getLoginDate(profile: Profile): number {
    return profile.loginDate;
  }

  getPositionInfo(position: Position): string {
    if (!position) {
      return '';
    }
    switch (position.category) {
      case PositionCategory.CIRCUIT:
        return 'agent:footer.circuit_position';
      case PositionCategory.MICROSOFT:
        return 'agent:footer.lync_position';
      case PositionCategory.OSV:
        return 'agent:footer.osv_position';
      case PositionCategory.PHONE:
      default:
        return `${position.site} - ${position.rawName} (${position.uri}) `;
    }
  }

  private toDefaultPath(initialView: 'infos' | 'center' | 'queues', channelQueues: QueueChannel[]): string {
    switch (initialView) {
      case 'center':
        return AgentDesktopState.CENTER;
      case 'queues':
        // it doesn't just depend on initialView, we must need to check for channelQueues
        return (channelQueues && channelQueues?.length) > 0 ? AgentDesktopState.QUEUES : AgentDesktopState.INFOS;
      case 'infos':
      default:
        return AgentDesktopState.INFOS;
    }
  }
}
